// Верхняя панель корневых вкладок. Закреплена сверху — не уезжает при прокрутке.
// Есть серверная шапка (config.navigation.headers.<вкладка>) — рисуем ЕЁ: слоты left · center · right, кнопки в порядке
// массива. С kind `title` — крупный заголовок. Незнакомый kind/action — молча пропускаем (Р-3).
// `visible: false` — шапки нет вовсе. Нет шапки или она пуста — своя: название вкладки + поиск + меню.
import { useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { track } from '../analytics/track';
import { t } from '../i18n/i18n';
import { paths } from '../nav';
import { haptic } from '../telegram/webapp';
import calendarDots from '../assets/icons/calendar-dots.svg';
import listIcon from '../assets/icons/list.svg';
import magnifyingGlass from '../assets/icons/magnifying-glass.svg';
import { setHeaderState, useHeaderState } from './headerState';
import { MaskIcon } from './Icon';
import styles from './AppBar.module.css';

const SLOTS = ['left', 'center', 'right'] as const;

/** Кнопка серверной шапки экрана (config.navigation.headers.<ключ вкладки>.items). */
export interface HeaderItem {
  key: string; kind: string; slot: string; label?: string; icon?: string;
  action?: { type: string; endpoint?: string; field?: string; source?: string };
}
export interface ServerHeader { items?: HeaderItem[]; visible?: boolean }
export type HeaderMode = 'none' | 'own' | 'server' | 'titled';

/** Высота шапки без safe area — в --appbar-height: под ней закрепляются полосы экранов. */
const HEIGHT: Record<HeaderMode, number> = { server: 64, titled: 52, own: 52, none: 0 };

/** Какую шапку рисовать: `visible: false` — никакой; нет/пусто/всё незнакомо — свою; есть `title` — крупный заголовок. */
export function headerMode(header: ServerHeader | undefined): HeaderMode {
  if (header?.visible === false) return 'none';
  const known = (header?.items ?? []).filter(isKnown);
  if (!known.length) return 'own';
  return known.some((i) => i.kind === 'title') ? 'titled' : 'server';
}

/** Что умеем нарисовать: остальное — молча мимо (контракт). */
export function isKnown(item: HeaderItem): boolean {
  const type = item.action?.type;
  if (!SLOTS.includes(item.slot as (typeof SLOTS)[number])) return false;
  switch (item.kind) {
    case 'toggle': return type === 'filter' && Boolean(item.action?.field);
    case 'date_picker': return type === 'date_picker';
    case 'title': return true;
    case 'search': return type === 'search';
    case 'menu': return type === 'settings';
    case 'button': return type === 'search' || type === 'settings';
    default: return false;
  }
}

export function AppBar({ title, header }: { title: string; header?: ServerHeader }) {
  const mode = headerMode(header);
  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--appbar-height', `${HEIGHT[mode]}px`);
  }, [mode]);
  if (mode === 'none') return null;
  if (mode === 'own') return <OwnBar title={title} />;
  const known = (header?.items ?? []).filter(isKnown);
  const titled = mode === 'titled';
  return (
    <header className={titled ? `${styles.bar} ${styles.titled}` : `${styles.bar} ${styles.server}`}>
      {SLOTS.map((slot) => {
        const items = known.filter((i) => i.slot === slot);
        if (!items.length) return slot === 'center' && !titled ? <span key={slot} className={styles.center} /> : null;
        const body = items.map((item) => <HeaderControl key={item.key} item={item} title={title} />);
        // Заголовок — вне капсулы, во всю свободную ширину
        if (titled && slot !== 'right') return <div key={slot} className={styles.center}>{body}</div>;
        // LIVE и календарь в макете — одна серая «капсула» слева
        return slot === 'left'
          ? <div key={slot} className={styles.capsule}>{body}</div>
          : <div key={slot} className={slot === 'center' ? styles.center : styles.right}>{body}</div>;
      })}
    </header>
  );
}

function SearchButton({ label }: { label?: string }) {
  const navigate = useNavigate();
  return (
    <button className={styles.icon} aria-label={label ?? t('shell.kind.search')} onClick={() => navigate(paths.search())}>
      <MaskIcon src={magnifyingGlass} size={22} />
    </button>
  );
}

function MenuButton({ label }: { label?: string }) {
  const navigate = useNavigate();
  return (
    <button className={styles.icon} aria-label={label ?? t('shell.menu')} onClick={() => navigate(paths.menu())}>
      <MaskIcon src={listIcon} size={24} />
    </button>
  );
}

function OwnBar({ title }: { title: string }) {
  return (
    <header className={styles.bar}>
      <h1 className={styles.title}>{title}</h1>
      <SearchButton />
      <MenuButton />
    </header>
  );
}

function HeaderControl({ item, title }: { item: HeaderItem; title: string }) {
  const navigate = useNavigate();
  const ui = useHeaderState();
  switch (item.kind) {
    case 'title':
      return <h1 className={styles.title}>{item.label || title}</h1>;
    case 'toggle':
      return (
        <button className={`${styles.live} ${ui.liveOnly ? styles.liveOn : ''}`} aria-pressed={ui.liveOnly}
          onClick={() => {
            haptic.selection();
            track('match_center_filter', { filter: ui.liveOnly ? 'all' : 'live' });
            setHeaderState({ liveOnly: !ui.liveOnly, liveField: item.action!.field! });
          }}>
          <span className={styles.liveDot} aria-hidden="true" />{item.label ?? 'LIVE'}
        </button>
      );
    case 'date_picker':
      return (
        <button className={styles.icon} aria-label={item.label ?? item.key} onClick={() => setHeaderState({ calendarOpen: true })}>
          <MaskIcon src={calendarDots} size={24} />
        </button>
      );
    case 'search':
      return (
        <button className={styles.searchField} onClick={() => navigate(paths.search())}>
          <MaskIcon src={magnifyingGlass} size={20} /><span>{item.label ?? t('shell.kind.search')}</span>
        </button>
      );
    case 'menu':
      return <MenuButton label={item.label} />;
    default:
      // kind button: лупа или меню шапок вкладок
      return item.action?.type === 'search' ? <SearchButton label={item.label} /> : <MenuButton label={item.label} />;
  }
}
